import type { LaunchStatus, ScheduleStatus, WorkStatus } from './types';

// Lokal kopia av Fontanas presentationspalett. Modulen importerar inte från Trends.
export const DISPLAY_FONT = '"Kapra Neue", "Oswald", sans-serif';

// Kulörerna har en stark nyans (markör, kant), en mörk nyans för text (minst 4,5:1 mot den ljusa)
// och en ljus nyans för ytor.
export const launchColors = {
  page: '#F7F5F0',
  card: '#FFFFFF',
  section: '#F7F5F0',
  divider: '#DDD8CD',
  rowDivider: 'rgba(23,44,99,0.08)',
  navy: '#172C63',
  navyLight: '#2D4580',
  softNavy: '#E3E7F0',
  text: '#2A2722',
  textSecondary: '#5A5549',
  textMuted: '#8A867F',
  neutralBg: '#F1EEE8',
  moss: '#4F893D',
  mossDark: '#33612A',
  mossLight: '#CDD9C3',
  plum: '#9C5479',
  plumDark: '#7A3A5C',
  plumLight: '#DDCDCE',
  sand: '#ACA289',
  sandDark: '#645839',
  sandLight: '#E1DDD2',
  petrol: '#669AA7',
  petrolDark: '#2A5C67',
  petrolLight: '#D2DDD9',
  warningText: '#8A5D00',
  warningBg: '#FFF4D6',
  warningMarker: '#C98A00',
  dangerText: '#9B2C2C',
  dangerBg: '#FDE8E8',
} as const;

export const phasePalette = ['#172C63', '#669AA7', '#9C5479', '#4F893D', '#ACA289', '#6F7F99'] as const;

export interface PillStyle {
  fg: string;
  bg: string;
  border?: string;
}

export const workStatusStyle: Record<WorkStatus, PillStyle> = {
  not_started: { fg: launchColors.textSecondary, bg: launchColors.card, border: `1px solid ${launchColors.divider}` },
  in_progress: { fg: '#FFFFFF', bg: launchColors.navyLight },
  in_review: { fg: launchColors.sandDark, bg: launchColors.sandLight, border: `1px solid ${launchColors.sand}` },
  changes_requested: { fg: launchColors.plumDark, bg: launchColors.plumLight, border: `1px solid ${launchColors.plum}` },
  done: { fg: launchColors.mossDark, bg: launchColors.mossLight },
};

export const scheduleStatusStyle: Record<ScheduleStatus, PillStyle> = {
  overdue: { fg: launchColors.dangerText, bg: launchColors.dangerBg },
  at_risk: { fg: launchColors.plumDark, bg: launchColors.plumLight },
  due_soon: { fg: launchColors.warningText, bg: launchColors.warningBg },
  on_track: { fg: launchColors.mossDark, bg: launchColors.mossLight },
  missing_due: { fg: launchColors.textSecondary, bg: launchColors.card, border: `1px dashed ${launchColors.textMuted}` },
  complete: { fg: launchColors.mossDark, bg: launchColors.mossLight },
};

export const scheduleMarkerColor: Record<ScheduleStatus, string> = {
  overdue: launchColors.dangerText,
  at_risk: launchColors.plum,
  due_soon: launchColors.warningMarker,
  on_track: launchColors.navy,
  missing_due: launchColors.textMuted,
  complete: launchColors.moss,
};

export const launchStatusStyle: Record<LaunchStatus, PillStyle & { stripe: string }> = {
  draft: { fg: launchColors.textSecondary, bg: launchColors.neutralBg, stripe: launchColors.textMuted },
  active: { fg: launchColors.mossDark, bg: launchColors.mossLight, stripe: launchColors.moss },
  paused: { fg: launchColors.warningText, bg: launchColors.warningBg, stripe: launchColors.sand },
  launched: { fg: launchColors.navy, bg: launchColors.softNavy, stripe: launchColors.navy },
  closed: { fg: launchColors.textSecondary, bg: launchColors.neutralBg, stripe: launchColors.divider },
  cancelled: { fg: launchColors.dangerText, bg: launchColors.dangerBg, stripe: launchColors.dangerText },
};

export const cardSx = {
  bgcolor: launchColors.card,
  border: `1px solid ${launchColors.divider}`,
  borderRadius: '12px',
  boxShadow: '0 1px 2px rgba(23,44,99,0.04)',
};

export const panelSx = {
  bgcolor: launchColors.card,
  border: `1px solid ${launchColors.divider}`,
  borderRadius: '12px',
  p: { xs: 1.5, sm: 2 },
};

export const cardHoverSx = {
  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(23,44,99,0.10)' },
};

export const pageTitleSx = {
  fontFamily: DISPLAY_FONT,
  fontSize: 28,
  fontWeight: 700,
  letterSpacing: '0.04em',
  lineHeight: 1.15,
  color: launchColors.navy,
  textTransform: 'uppercase' as const,
};

export const sectionTitleSx = {
  fontSize: 18,
  fontWeight: 700,
  color: launchColors.navy,
  lineHeight: 1.3,
};

export const overlineSx = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: launchColors.textSecondary,
  textTransform: 'uppercase' as const,
};

export const chipButtonSx = (active: boolean) => ({
  height: 32,
  px: 1.75,
  borderRadius: '16px',
  fontSize: 13,
  fontWeight: 600,
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
  bgcolor: active ? launchColors.navy : launchColors.card,
  color: active ? '#FFFFFF' : launchColors.navy,
  border: `1px solid ${active ? launchColors.navy : launchColors.divider}`,
  '&:hover': { bgcolor: active ? launchColors.navyLight : launchColors.softNavy },
});
